import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import '@fortawesome/fontawesome-free/css/all.min.css';
import HelpTooltip from './HelpTooltip';
import HelpModal from './HelpModal';

/**
 * HelpIcon - A question mark icon that shows tooltip on hover and detailed modal on click
 * @param {string} content - Short tooltip text (1-2 sentences)
 * @param {string|JSX} detailedContent - Full modal content (can be string or JSX)
 * @param {string} position - Tooltip position ('top', 'bottom', 'left', 'right', default: 'top')
 * @param {number} size - Icon size in pixels (default: 16)
 * @param {string} className - Additional CSS classes
 */
export default function HelpIcon({ 
  content, 
  detailedContent, 
  position = 'top', 
  size = 16,
  className = ''
}) {
  const { t } = useTranslation();
  const [showTooltip, setShowTooltip] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const iconRef = useRef(null);
  const timeoutRef = useRef(null);

  // Handle hover with delay for better UX
  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setShowTooltip(true);
    }, 300);
  };

  const handleMouseLeave = (e) => {
    // Add a small delay before hiding to allow moving to tooltip
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setShowTooltip(false);
    }, 100);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleClick = (e) => {
    e.stopPropagation();
    setShowTooltip(false);
    setShowModal(true);
  };

  // Don't render if no content provided
  if (!content && !detailedContent) {
    return null;
  }

  return (
    <>
      <span
        ref={iconRef}
        className={className}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginLeft: 6,
          cursor: 'pointer',
          color: '#64748b',
          fontSize: size,
          width: size + 4,
          height: size + 4,
          minWidth: size + 4,
          minHeight: size + 4,
          flexShrink: 0,
          borderRadius: '50%',
          transition: 'color 0.2s, background-color 0.2s',
          verticalAlign: 'middle',
          position: 'relative',
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        onTouchStart={(e) => {
          e.stopPropagation();
          // On mobile, show modal directly
          setShowModal(true);
        }}
        aria-label={t('help.ariaLabel')}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick(e);
          }
        }}
      >
        <i className="fas fa-question-circle" style={{ display: 'block', lineHeight: 1 }}></i>
      </span>
      
      {showTooltip && content && iconRef.current && (
        <HelpTooltip
          content={content}
          position={position}
          anchorElement={iconRef.current}
          onClose={() => setShowTooltip(false)}
        />
      )}

      {showModal && detailedContent && (
        <HelpModal
          content={detailedContent}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
}

